import type { PartialRecord } from '@/core/helperTypes'
import type { ConsensusRasterRemap } from '@/modules/Editor/models/raster/consensusRasterRemap'
import type { View } from '@/modules/Editor/views/view'
import type { V2AnnotationPayload } from '@/store/types/StageAnnotationPayload'

import { createRasterFromDeserializable } from './createRasterFromDeserializable'
import { isViewsAnnotation } from '@/modules/Workview/isViewsAnnotation'
import { isMaskAnnotationData } from '@/modules/Editor/models/annotation/annotationKindValidator'

const isImageRasterLayerAnnotation = (annotation: V2AnnotationPayload): boolean =>
  'raster_layer' in annotation.data && annotation.data.raster_layer !== undefined

export const isVideoRasterLayerAnnotation = (annotation: V2AnnotationPayload): boolean => {
  if (!('frames' in annotation.data)) {
    return false
  }

  const frameIndicies = Object.keys(annotation.data.frames)
  const firstFrame = annotation.data.frames[frameIndicies[0]] || {}

  if (!('raster_layer' in firstFrame) || firstFrame.raster_layer === undefined) {
    return false
  }

  return true
}

export const isRasterLayerAnnotation = (annotation: V2AnnotationPayload): boolean =>
  isImageRasterLayerAnnotation(annotation) || isVideoRasterLayerAnnotation(annotation)
/**
 * If a raster layer doesn't yet exist on the image, sets up the raster
 * and adds it to the RasterManager.
 *
 * Default-group raster layers are processed first so the writable raster
 * exists before any non-default consensus overlays are created on top.
 *
 * `consensusRemap` only applies to overlays from groups other than the
 * current user's: in the parallel/annotate stage the annotator's own
 * raster has a non-null group id but its masks are originals (no
 * default-group copy), so remapping would drop every label.
 */
const initialiseRastersForView = (
  view: View,
  rasterLayerAnnotations: V2AnnotationPayload[],
  maskAnnotations: PartialRecord<string, V2AnnotationPayload>,
  consensusRemap: ConsensusRasterRemap,
  currentUserAnnotationGroupId: string | null,
): void => {
  const sorted = [...rasterLayerAnnotations].sort((a, b) => {
    const aDefault = (a.annotation_group_id ?? null) === null ? 0 : 1
    const bDefault = (b.annotation_group_id ?? null) === null ? 0 : 1
    return aDefault - bDefault
  })

  sorted.forEach((rasterLayerAnnotation) => {
    const groupId = rasterLayerAnnotation.annotation_group_id ?? null
    const isOwnedGroup = groupId === null || groupId === currentUserAnnotationGroupId
    createRasterFromDeserializable(
      view,
      rasterLayerAnnotation,
      maskAnnotations,
      isOwnedGroup ? undefined : consensusRemap,
    )
  })
}

/**
 * Scans through given annotations and creates a mapping of raster masks,
 * while using the already cached mapping on the view to reduce the amount of work needed.
 */
export const resolveRasterMapping = (
  view: View,
  newAnnotations: V2AnnotationPayload[],
  rasterLayers: V2AnnotationPayload[],
  currentUserAnnotationGroupId: string | null,
): V2AnnotationPayload[] => {
  const regularAndMaskAnnotations: V2AnnotationPayload[] = []
  // We use a map for this as its quicker to consume as each element is looked for once by id.
  const maskAnnotationMap: PartialRecord<string, V2AnnotationPayload> = {}
  // Remap for non-default-group rasters in consensus review: each mask copied
  // into the default group from a workflow group keeps `source_annotation_id`
  // pointing at the original. Non-default raster mappings reference those
  // originals; we rewrite them to the corresponding default-group ids.
  const sourceIdToDefaultId = new Map<string, string>()

  newAnnotations.forEach((storeAnnotation) => {
    if (!isViewsAnnotation(view.fileManager.slotName, storeAnnotation)) {
      return
    }

    // Seperate out rasters, and make multiple annotations from these rasters
    if (!isRasterLayerAnnotation(storeAnnotation)) {
      regularAndMaskAnnotations.push(storeAnnotation)

      if (isMaskAnnotationData(storeAnnotation.data)) {
        maskAnnotationMap[storeAnnotation.id] = storeAnnotation

        const annotationGroupId = storeAnnotation.annotation_group_id ?? null
        if (annotationGroupId === null && storeAnnotation.source_annotation_id) {
          sourceIdToDefaultId.set(storeAnnotation.source_annotation_id, storeAnnotation.id)
        }
      }
    }
  })

  view.rasterAnnotationLayer.clear()

  initialiseRastersForView(
    view,
    rasterLayers.filter((raster) => isViewsAnnotation(view.name, raster)),
    maskAnnotationMap,
    { sourceIdToDefaultId },
    currentUserAnnotationGroupId,
  )

  return regularAndMaskAnnotations
}
